import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Check } from 'lucide-react';

import { useExport } from '../../../../context/useExport';
import { useAppSelector } from '../../../../hooks';
import { getNorms } from '../../../../store/selectors/user.selector';
import { selectActiveVersion } from '../../../../store/selectors/workspace.selector.ts';
import { createLayerTitle, getIlluminatedLayers } from '../../../../utils/pdfUtils';
import { PanelSearch, PanelSection } from '../PanelSection';

function CheckRow({ label, checked, onToggle }: { label: string; checked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      className="w-full flex items-center gap-3 h-9 px-4 text-left hover:bg-white/[0.04] transition-colors"
    >
      <span
        className={`h-4 w-4 shrink-0 rounded-sm flex items-center justify-center transition-colors ${
          checked ? 'bg-lime-400' : 'bg-black border border-white/20'
        }`}
      >
        {checked && <Check className="h-3 w-3 text-black" />}
      </span>
      <span className="min-w-0 truncate text-[13px] text-gray-200">{label}</span>
    </button>
  );
}

/** What goes into the PDF; the preview fills the canvas and the download sits in the footer. */
export function ExportSection() {
  const { t } = useTranslation();
  const norms = useSelector(getNorms);
  const version = useAppSelector(selectActiveVersion);
  const { selectedMeasurements, handleMeasurementCheck, checkedItems, handleCheck } = useExport();
  const [search, setSearch] = useState('');

  const items = [
    { id: 'cover-page', label: t('workspace.export.items.coverPage') },
    { id: 'table-of-content', label: t('workspace.export.items.tableOfContents') },
    { id: 'lamp-info', label: t('workspace.export.items.luminaireInfo') },
    { id: 'layers-summary', label: t('workspace.export.items.layersSummary') },
    { id: 'graphs', label: t('workspace.export.items.layerPlots') },
    { id: 'global-plots', label: t('workspace.export.items.globalPlots') },
  ];

  const measurementTitles = version
    ? getIlluminatedLayers(version).map((mesh) =>
        createLayerTitle(mesh.displayName, mesh.normId, norms, mesh.targets?.length ? 'custom' : undefined),
      )
    : [];
  const q = search.trim().toLowerCase();
  const filtered = q ? measurementTitles.filter((title) => title.toLowerCase().includes(q)) : measurementTitles;

  return (
    <PanelSection title={t('workspace.export.documentation')}>
      <h3 className="px-4 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {t('workspace.structure')}
      </h3>
      {items.map((item) => (
        <CheckRow
          key={item.id}
          label={item.label}
          checked={!!checkedItems[item.id]}
          onToggle={() => handleCheck(item.id)}
        />
      ))}

      <h3 className="px-4 pt-4 pb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {t('workspace.measurements')}
        {q && (
          <span className="ml-2 normal-case tracking-normal text-lime-400">
            {filtered.length}/{measurementTitles.length}
          </span>
        )}
      </h3>
      {measurementTitles.length > 6 && (
        <PanelSearch value={search} onChange={setSearch} placeholder={t('workspace.searchMeasurements')} />
      )}
      {filtered.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-gray-400">
          {q ? t('workspace.noMeasurementsMatch') : t('workspace.noMeasurementsAvailable')}
        </p>
      ) : (
        <div className="pb-3">
          {filtered.map((title) => (
            <CheckRow
              key={title}
              label={title}
              checked={!!selectedMeasurements[title]}
              onToggle={() => handleMeasurementCheck(title)}
            />
          ))}
        </div>
      )}
    </PanelSection>
  );
}
